import typeToReducer from 'type-to-reducer';
import get from 'lodash.get';
import capitalize from 'lodash.capitalize';

import {
  CURRENCY_NATIVE,
  DEFAULT_EXCHANGE,
  DEFAULT_MINER,
  DEFAULT_MOVING_MATH,
  DEFAULT_SCALE,
  DEFAULT_COLOR,
  DEFAULT_RESOLUTION,
} from '../../constants/app';

import { RESET_STORE } from '../resetStore/actions';

import * as actions from './actions';

const createTrace = (asset, metricCode, options = {}) => ({
  asset,
  currency: CURRENCY_NATIVE,
  color: DEFAULT_COLOR,
  data: [],
  err: false,
  errMsg: '',
  exchange: DEFAULT_EXCHANGE,
  loading: false,
  metricCode,
  miner: DEFAULT_MINER,
  movingAverage: DEFAULT_MOVING_MATH,
  movingMedian: DEFAULT_MOVING_MATH,
  resolution: DEFAULT_RESOLUTION,
  scale: DEFAULT_SCALE,
  dependency: {
    data: {},
    err: false,
    errMsg: '',
    loading: false,
  },
  ...options,
});

const updateTrace = (idx, input) => (item, index) => {
  if (index === idx) {
    return {
      ...item,
      ...input,
    };
  }

  return item;
};

const initialState = {
  traces: [],
};

export default typeToReducer(
  {
    [actions.GET_TRACE]: {
      PENDING: (state, action) => {
        const { idx, metricCode } = action.payload;

        const traces = state.traces.map(
          updateTrace(idx, {
            data: [],
            err: false,
            loading: true,
            metricCode,
          }),
        );

        return {
          ...state,
          traces,
        };
      },
      FULFILLED: (state, action) => {
        const { idx, data } = action.payload;

        const traces = state.traces.map(
          updateTrace(idx, {
            data,
            loading: false,
          }),
        );

        return {
          ...state,
          traces,
        };
      },
      REJECTED: (state, action) => {
        const { idx, e } = action.payload;

        const response = get(e, 'response', {});
        const errMsg = capitalize(response.data);

        const traces = state.traces.map(
          updateTrace(idx, {
            loading: false,
            err: true,
            errMsg,
          }),
        );

        return {
          ...state,
          traces,
        };
      },
    },

    [actions.GET_DEPENDENCY]: {
      PENDING: (state, action) => {
        const { idx, metricCode } = action.payload;

        const traces = state.traces.map(
          updateTrace(idx, {
            dependency: {
              data: {},
              err: false,
              loading: true,
              metricCode,
            },
          }),
        );

        return {
          ...state,
          traces,
        };
      },
      FULFILLED: (state, action) => {
        const { idx, data } = action.payload;

        const traces = state.traces.map(
          updateTrace(idx, {
            dependency: {
              data,
              loading: false,
            },
          }),
        );

        return {
          ...state,
          traces,
        };
      },
      REJECTED: (state, action) => {
        const { idx, e } = action.payload;

        const response = get(e, 'response', {});
        const errMsg = capitalize(response.data);

        const traces = state.traces.map(
          updateTrace(idx, {
            dependency: {
              loading: false,
              err: true,
              errMsg,
            },
          }),
        );

        return {
          ...state,
          traces,
        };
      },
    },

    [actions.ADD_TRACE]: (state, action) => {
      const { traces: _traces } = state;
      const traces = _traces.slice();

      const { asset, metricCode, options } = action.payload;
      const trace = createTrace(asset, metricCode, options);
      traces.push(trace);

      return {
        ...state,
        traces,
      };
    },

    [actions.DELETE_TRACE]: (state, action) => {
      const { idx } = action.payload;

      const traces = state.traces.filter((_, index) => index !== idx);

      return {
        ...state,
        traces,
      };
    },

    [actions.RESET_TRACES]: () => initialState,

    [actions.CHANGE_ASSET]: (state, action) => {
      const { idx, asset } = action.payload;

      const traces = state.traces.map(
        updateTrace(idx, {
          asset,
          data: [],
        }),
      );

      return {
        ...state,
        traces,
      };
    },

    [actions.CHANGE_METRIC]: (state, action) => {
      const { idx, metricCode } = action.payload;

      const traces = state.traces.map(
        updateTrace(idx, {
          metricCode,
          data: [],
        }),
      );

      return {
        ...state,
        traces,
      };
    },

    [actions.CHANGE_SCALE]: (state, action) => {
      const { idx, value } = action.payload;

      const traces = state.traces.map(
        updateTrace(idx, {
          scale: value,
        }),
      );

      return {
        ...state,
        traces,
      };
    },

    [actions.CHANGE_MOVING_AVERAGE]: (state, action) => {
      const { idx, value } = action.payload;

      const traces = state.traces.map(
        updateTrace(idx, {
          movingAverage: value,
          movingMedian: 0,
        }),
      );

      return {
        ...state,
        traces,
      };
    },

    [actions.CHANGE_MOVING_MEDIAN]: (state, action) => {
      const { idx, value } = action.payload;

      const traces = state.traces.map(
        updateTrace(idx, {
          movingMedian: value,
          movingAverage: 0,
        }),
      );

      return {
        ...state,
        traces,
      };
    },

    [actions.CHANGE_CURRENCY]: (state, action) => {
      const { idx, value } = action.payload;

      const traces = state.traces.map(
        updateTrace(idx, {
          currency: value,
        }),
      );

      return {
        ...state,
        traces,
      };
    },

    [actions.CHANGE_EXCHANGE]: (state, action) => {
      const { idx, value } = action.payload;

      const traces = state.traces.map(
        updateTrace(idx, {
          exchange: value,
        }),
      );

      return {
        ...state,
        traces,
      };
    },

    [actions.RESET_EXCHANGE]: (state, action) => {
      const { idx } = action.payload;

      const traces = state.traces.map(
        updateTrace(idx, {
          exchange: DEFAULT_EXCHANGE,
        }),
      );

      return {
        ...state,
        traces,
      };
    },

    [actions.CHANGE_MINER]: (state, action) => {
      const { idx, value } = action.payload;

      const traces = state.traces.map(
        updateTrace(idx, {
          miner: value,
        }),
      );

      return {
        ...state,
        traces,
      };
    },

    [actions.RESET_MINER]: (state, action) => {
      const { idx } = action.payload;

      const traces = state.traces.map(
        updateTrace(idx, {
          miner: DEFAULT_MINER,
        }),
      );

      return {
        ...state,
        traces,
      };
    },

    [actions.CHANGE_COLOR]: (state, action) => {
      const { idx, color } = action.payload;

      const traces = state.traces.map(
        updateTrace(idx, {
          color,
        }),
      );

      return {
        ...state,
        traces,
      };
    },

    [RESET_STORE]: () => ({
      ...initialState,
    }),
  },
  initialState,
);
